import type { App } from "@open-booking/server";
import type { CautionStatus, CreateCautionPayload, ReleaseCautionPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";
import { tenantApi } from "@/src/owner/services/tenant-http.service";

type ListResponse = App["~Routes"]["cautions"]["get"]["response"]["200"];
type DetailResponse = App["~Routes"]["cautions"][":id"]["get"]["response"]["200"];
type CreateResponse = App["~Routes"]["cautions"]["post"]["response"]["200"];
type ReleaseResponse = App["~Routes"]["cautions"][":id"]["release"]["post"]["response"]["200"];
type BadgeResponse = App["~Routes"]["cautions"]["badge-count"]["get"]["response"]["200"];
type ResendEmailResponse = App["~Routes"]["cautions"][":id"]["resend-email"]["post"]["response"]["200"];
type CancelResponse = App["~Routes"]["cautions"][":id"]["cancel"]["post"]["response"]["200"];

export const cautionApi = createApi({
	reducerPath: "cautionApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Caution", "CautionList", "CautionBadge"],
	endpoints: (builder) => ({
		// biome-ignore lint/suspicious/noConfusingVoidType: RTK Query uses `| void` so the hook accepts no argument
		getCautions: builder.query<ListResponse, { status?: CautionStatus; search?: string } | void>({
			query:
				(params): EdenCall<ListResponse> =>
				(client) =>
					client.cautions.get({ query: params ?? {} }),
			providesTags: ["CautionList"],
		}),
		getCautionDetail: builder.query<DetailResponse, string>({
			query:
				(id): EdenCall<DetailResponse> =>
				(client) =>
					client.cautions({ id }).get(),
			providesTags: (_result, _error, id) => [{ type: "Caution", id }],
		}),
		getCautionsBadgeCount: builder.query<BadgeResponse, void>({
			query: (): EdenCall<BadgeResponse> => (client) => client.cautions["badge-count"].get(),
			providesTags: ["CautionBadge"],
		}),
		createCaution: builder.mutation<CreateResponse, CreateCautionPayload>({
			query:
				(payload): EdenCall<CreateResponse> =>
				(client) =>
					client.cautions.post(payload),
			invalidatesTags: ["CautionList", "CautionBadge"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					// A new tenant may have been linked to the owner address book.
					dispatch(tenantApi.util.invalidateTags(["Tenant"]));
				} catch {
					// no-op: cache stays untouched on error
				}
			},
		}),
		releaseCaution: builder.mutation<ReleaseResponse, { id: string; payload: ReleaseCautionPayload }>({
			query:
				({ id, payload }): EdenCall<ReleaseResponse> =>
				(client) =>
					client.cautions({ id }).release.post(payload),
			invalidatesTags: (_result, _error, { id }) => [{ type: "Caution", id }, "CautionList", "CautionBadge"],
		}),
		resendCautionEmail: builder.mutation<ResendEmailResponse, string>({
			query:
				(id): EdenCall<ResendEmailResponse> =>
				(client) =>
					client.cautions({ id })["resend-email"].post(),
			invalidatesTags: (_result, _error, id) => [{ type: "Caution", id }],
		}),
		cancelCaution: builder.mutation<CancelResponse, string>({
			query:
				(id): EdenCall<CancelResponse> =>
				(client) =>
					client.cautions({ id }).cancel.post(),
			invalidatesTags: (_result, _error, id) => [{ type: "Caution", id }, "CautionList", "CautionBadge"],
		}),
	}),
});

export const {
	useGetCautionsQuery,
	useGetCautionDetailQuery,
	useGetCautionsBadgeCountQuery,
	useCreateCautionMutation,
	useReleaseCautionMutation,
	useResendCautionEmailMutation,
	useCancelCautionMutation,
} = cautionApi;
